import { ElementsPanelProps } from '@wix/editor-platform-feature-store';
import { IElementItemsBuilder, ResolverApis } from './types';
import {
  ActionName,
  IActionableElementsResult,
  IVirtualManifestBuilderProps,
  ManifestComponentExtensionType,
  NativeActionsList,
} from '@wix/editor-platform-manifest-services';
import { PLATFORM_BREAKPOINT_RANGES } from '@wix/editor-platform-host-integration-apis';
import { LAYOUT } from '@wix/component-protocol/schema';
import { buildManifestFromOOIMetadata } from './utils/buildManifestFromOOIMetadata';
import { CompRef } from '@wix/document-services-types';
import { MetadataStore } from './MetadataStore';
import { DisplayElementsPanelVisibilityToggler } from './DisplayElementsPanelVisibilityToggler';
import { OOIElementItemsBuilder, BuilderElementItemsBuilder } from './builders';

import { EditorPointer } from '@wix/editor-document-logic-api';

export class DisplayElementsPanelService {
  #editorPointer;
  #contextBuilder;
  #metadataStore;
  #elementsVisibilityData: Record<string, boolean> = {};
  #respectIndeterminate = true;
  #displayElementsPanelVisibilityToggler;
  #extensionType;
  #elementItemsBuilder: IElementItemsBuilder;

  constructor(private readonly apis: ResolverApis) {
    this.#editorPointer = this.apis.editorPointer;
    this.#metadataStore = new MetadataStore(this.apis.editorPointerAPI);

    const api = this.apis.componentsAPI.buildManifestAPI({
      editorPointers: [this.#editorPointer],
    });
    this.#extensionType = api.node.getExtensionType();

    this.#contextBuilder = this.#getContextBuilder();

    this.#elementItemsBuilder = this.#getElementItemsBuilder();

    this.#displayElementsPanelVisibilityToggler =
      new DisplayElementsPanelVisibilityToggler(this.apis, this.#metadataStore);
  }

  #getElementItemsBuilder() {
    return this.#extensionType === ManifestComponentExtensionType.OOI
      ? new OOIElementItemsBuilder(
          this.apis.editorPointerAPI,
          this.#metadataStore,
          this.#editorPointer,
        )
      : new BuilderElementItemsBuilder(
          this.apis.editorPointerAPI,
          this.apis.componentsAPI.behaviors,
          this.#metadataStore,
        );
  }

  #collectItems(): ElementsPanelProps['items'] {
    const actionableElements = this.getActionableElements();

    if (!actionableElements?.elements.length) {
      return [];
    }

    const api = this.apis.componentsAPI.buildManifestAPI({
      editorPointers: [this.#editorPointer],
      contextBuilder: this.#contextBuilder,
    });

    return this.#elementItemsBuilder.buildElementItems(
      actionableElements.elements,
      api,
      1,
    );
  }

  #getContextBuilder(): IVirtualManifestBuilderProps['contextBuilder'] {
    const api = this.apis.componentsAPI.buildManifestAPI({
      editorPointers: [this.#editorPointer],
    });

    const metadata = api.node.getManifestMetadata()?.statics;

    if (
      api.node.getExtensionType() !== ManifestComponentExtensionType.OOI ||
      !metadata
    ) {
      return;
    }

    const currentBreakpoint =
      this.apis.platformApiMethods.editor.stage?.getBreakpointFromPointer(
        this.#editorPointer,
        PLATFORM_BREAKPOINT_RANGES,
      );
    const isMobile = currentBreakpoint === LAYOUT.BREAKPOINT.small;

    const ooiManifest = buildManifestFromOOIMetadata(metadata, isMobile);

    return (builder) =>
      builder.withEditorElement(this.#editorPointer, ooiManifest);
  }

  getContextBuilder() {
    return this.#contextBuilder;
  }

  getActionableElements(): IActionableElementsResult | null {
    return this.apis.componentsAPI.actionableElements.getActionableElements(
      [this.#editorPointer],
      NativeActionsList.elements as ActionName,
      this.#contextBuilder,
    );
  }

  #getData() {
    return Object.entries(this.#metadataStore.getStore()).reduce<
      Record<string, boolean>
    >((acc, [id, { key, namespace, visibilityEditorPointer }]) => {
      return {
        ...acc,
        [id]: this.apis.componentsAPI.displayVisibility.isVisibleElementOnStage(
          visibilityEditorPointer,
          namespace === 'cssCustomProperties' ? key : undefined,
        ),
      };
    }, {});
  }

  setRespectIndeterminate(value: boolean) {
    this.#respectIndeterminate = value;
  }

  getProps(): ElementsPanelProps {
    const variantPointer =
      this.apis.platformApiMethods.editor.stage?.getPointerWithCurrentVariants(
        this.#editorPointer,
      ) as EditorPointer<CompRef> | undefined;

    if (variantPointer) {
      this.#editorPointer = variantPointer;
      this.#contextBuilder = this.#getContextBuilder();
      this.#elementItemsBuilder = this.#getElementItemsBuilder();
    }

    const items = this.#collectItems();
    this.#elementsVisibilityData = this.#getData();

    return {
      items,
      data: this.#elementsVisibilityData,
      onToggleState: async (id, value) => {
        const result =
          this.#displayElementsPanelVisibilityToggler.onToggleState(
            id,
            value,
            this.#respectIndeterminate,
            {
              editorPointers: [this.#editorPointer],
              contextBuilder: this.#contextBuilder,
            },
            this.#elementsVisibilityData,
          );

        this.#elementsVisibilityData = {
          ...this.#elementsVisibilityData,
          ...result,
        };

        return result;
      },
    };
  }
}
